import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'reactstrap'
import { epicActions } from '../../redux/actions'

import MyRewardsErrorModal from '../Modal/MyRewardsErrorModal/MyRewardsErrorModal'
import MyRewardsSuccessModal from '../Modal/MyRewardsSuccessModal/MyRewardsSuccessModal'
import FormattedMessage from '../FormattedMessage/FormattedMessage'
import Loader from '../Loader/Loader'

const epicTicket = 'epic_v1'
const EPIC_AUTH_CHECK_INTERVAL = 1000
const EPIC_AUTH_TIMEOUT = 30000

function EpicIntegration(props) {
  const dispatch = useDispatch()
  const [btnText, setBtnText] = useState('epic.button.logIn')
  const [loading, setLoading] = useState(false)
  const [isEpicAuthenticated, setIsEpicAuthenticated] = useState(false)
  const [isRedeemed, setIsRedeemed] = useState(false)
  const { couponCode, contentId, contentName } = props
  const [epicError, setEpicError] = useState(false)
  const [epicSuccess, setEpicSuccess] = useState(false)
  const [epicErrorString, setEpicErrorString] = useState(null)

  const authenticated = useSelector(state => state.epicReducer.authenticated)
  const accessToken = useSelector(state => state.epicReducer.accessToken)

  useEffect(() => {
    setIsEpicAuthenticated(authenticated)

    if (isRedeemed) {
      setBtnText('epic.button.play')
    } else if (authenticated) {
      setBtnText('epic.button.activate')
    } else {
      setBtnText('epic.button.logIn')
    }
  }, [isRedeemed, authenticated, accessToken])

  const handleBtnAction = () => {
    if (isRedeemed) {
      const externalUrl = 'https://store.epicgames.com'
      return window.open(externalUrl)
    }
    if (authenticated) {
      activateGame()
    } else {
      // Not authenticated yet
      onEpicAuth()
    }
  }

  const waitForEpicAuth = popup =>
    new Promise(resolve => {
      const startedAt = Date.now()
      const interval = setInterval(() => {
        const storedTicket = window.localStorage.getItem(epicTicket)
        const authTimedOut = Date.now() - startedAt >= EPIC_AUTH_TIMEOUT

        if (storedTicket || (popup && popup.closed) || authTimedOut) {
          clearInterval(interval)
          resolve(storedTicket)
        }
      }, EPIC_AUTH_CHECK_INTERVAL)
    })

  const toggleEpicError = () => {
    setEpicError(!epicError)
  }

  const toggleEpicSuccess = () => {
    setEpicSuccess(!epicSuccess)
  }

  const activateGame = () => {
    setLoading(false)
    if (isEpicAuthenticated) {
      dispatch(epicActions.activateGame(contentId, couponCode))
        .then(response => {
          // if successful response
          if (response && response.epicTxnId) {
            setLoading(false)
            setIsRedeemed(true)
            setEpicSuccess(true)
          } else {
            setLoading(false)
            setIsRedeemed(false)
          }
        })
        .catch(error => {
          setLoading(false)
          setIsRedeemed(false)
          setEpicError(true)
          setEpicErrorString(error.errors)
        })
    }
  }

  const onEpicAuth = async () => {
    setLoading(true)
    const redirectUri = `${encodeURI(document.location.protocol)}//${encodeURI(
      document.location.host
    )}/epic-redirect`
    // const redirectUri = 'https://newqa.phoenixfms.ca/epic-redirect'
    const clientId = `${process.env.RAZZLE_EPIC_CLIENT_ID}`
    const src = `https://www.epicgames.com/id/authorize?client_id=${clientId}&response_type=code&scope=basic_profile&redirect_uri=${redirectUri}`

    const popup = window.open(src, 'Epic Oauth', 'width=700,height=900')
    if (!popup) {
      setLoading(false)
      setEpicError(true)
      setEpicErrorString('error.epic.reauthenticationRequired')
      return
    }

    const storedTicket = await waitForEpicAuth(popup)
    if (storedTicket) {
      setLoading(false)

      if (!authenticated) {
        dispatch(epicActions.saveAuthUser(storedTicket))
      }
    } else {
      dispatch(epicActions.removeAuthUser())
      setLoading(false)
      setEpicError(true)
      setEpicErrorString('error.epic.reauthenticationRequired')
    }
  }

  const handleLogout = () => dispatch(epicActions.removeAuthUser())

  let statusMessage = 'Not logged in'
  if (isRedeemed) {
    statusMessage = 'Game activated successfully'
  } else if (authenticated) {
    statusMessage = 'Logged into Epic Games'
  }

  if (loading) {
    return <Loader button />
  }

  return (
    <>
      <div className="mb-3">
        <div className="row">
          <div className="col">
            <Button
              color="dark"
              block
              outline
              onClick={handleBtnAction}
              aria-describedby="epic-integration-status"
            >
              <FormattedMessage id={btnText} />
            </Button>
            {authenticated && accessToken && (
              <Button
                color="link"
                className="logout p-0 border-0"
                onClick={handleLogout}
                aria-label="Logout from Epic account"
                style={{ textDecoration: 'none' }}
              >
                <u>
                  <FormattedMessage id="epic.button.logout" />
                </u>
              </Button>
            )}
          </div>
        </div>
      </div>
      <div id="epic-integration-status" className="sr-only">
        {statusMessage}
      </div>
      <MyRewardsErrorModal
        isOpen={epicError}
        toggle={toggleEpicError}
        error={epicErrorString}
        platform="epic"
      />
      <MyRewardsSuccessModal
        isOpen={epicSuccess}
        toggle={toggleEpicSuccess}
        contentName={contentName}
        platform="epic"
      />
    </>
  )
}

export default EpicIntegration
